import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { quizQuestions, quizStyles, type QuizOption } from '@data/gardenStyleQuiz';

type GardenStyleQuizProps = {
	tryQuestionnaireHref?: string;
	designCtaLabel?: string;
	retakeLabel?: string;
	yourStyleLabel?: string;
	questionOfLabel?: (current: number, total: number) => string;
	onAnswer?: (step: number, option: QuizOption) => void;
	onRetake?: () => void;
};

export default function GardenStyleQuiz({
	tryQuestionnaireHref = '/try-questionnaire',
	designCtaLabel = 'Design my garden',
	retakeLabel = 'Retake quiz',
	yourStyleLabel = 'Your style is',
	questionOfLabel = (current, total) => `Question ${current} of ${total}`,
	onAnswer,
	onRetake
}: GardenStyleQuizProps) {
	const [quizStep, setQuizStep] = useState(0);
	const [quizAnswers, setQuizAnswers] = useState<QuizOption[]>([]);

	const quizResult = useMemo(() => {
		if (quizAnswers.length !== quizQuestions.length) return null;
		const scores = { romantic: 0, sculptural: 0, earthy: 0, zen: 0 };
		quizAnswers.forEach((answer) => {
			(Object.keys(scores) as (keyof typeof scores)[]).forEach((key) => {
				scores[key] += answer.scores[key] || 0;
			});
		});
		const winner = (Object.entries(scores) as [keyof typeof scores, number][]).sort((a, b) => b[1] - a[1])[0][0];
		return quizStyles[winner];
	}, [quizAnswers]);

	const handleQuizAnswer = (option: QuizOption) => {
		if (quizStep >= quizQuestions.length) return;
		onAnswer?.(quizStep + 1, option);
		const nextAnswers = [...quizAnswers, option];
		setQuizAnswers(nextAnswers);
		setQuizStep((s) => Math.min(s + 1, quizQuestions.length));
	};

	const handleRetake = () => {
		onRetake?.();
		setQuizAnswers([]);
		setQuizStep(0);
	};

	return (
		<div className="seeker-quiz-shell mx-auto">
			<div className="seeker-quiz-progress mb-4">
				{quizQuestions.map((_, idx) => (
					<div key={idx} className={`seeker-quiz-bar ${quizStep > idx ? 'is-done' : ''}`} />
				))}
			</div>
			{quizResult ? (
				<div className="seeker-quiz-result text-center">
					<div className="fs-2 mb-2">{quizResult.emoji}</div>
					<div className="text-uppercase small fw-semibold text-success-emphasis opacity-75 mb-2">{yourStyleLabel}</div>
					<h3 className="h2 text-success-emphasis mb-2">{quizResult.title}</h3>
					<p className="text-muted mb-4">{quizResult.description}</p>
					<div className="d-flex flex-column flex-sm-row justify-content-center gap-2">
						<Link to={tryQuestionnaireHref} className="btn btn-success rounded-pill px-4">
							{designCtaLabel}
						</Link>
						<button type="button" className="btn btn-outline-success rounded-pill px-4" onClick={handleRetake}>
							{retakeLabel}
						</button>
					</div>
				</div>
			) : (
				<>
					<div className="text-center mb-4">
						<div className="fs-4 mb-2" aria-hidden>
							{quizQuestions[quizStep].icon}
						</div>
						<h3 className="h3 text-success-emphasis">{quizQuestions[quizStep].prompt}</h3>
						<div className="small text-muted">
							{questionOfLabel(quizStep + 1, quizQuestions.length)}
						</div>
					</div>
					<div className="row g-3">
						{quizQuestions[quizStep].options.map((option) => (
							<div key={option.label} className="col-12 col-md-6">
								<button
									type="button"
									className="seeker-vibe-card rounded-4 p-3 w-100 text-center border-0"
									onClick={() => handleQuizAnswer(option)}
								>
									<div className="fs-4 mb-2" aria-hidden>
										{option.emoji}
									</div>
									<div className="small fw-semibold text-success-emphasis">{option.label}</div>
								</button>
							</div>
						))}
					</div>
				</>
			)}
		</div>
	);
}
